import {
  buildPresetHref,
  getPhotoEditorJtbdCategories,
  getPhotoEditorPreviewPoster,
  getToolsForCategory,
  type PhotoEditorTool,
} from "@/app/editor/data/ai-photo-editor";
import {
  getHubBrandingLookCards,
  getHubFreeToolCards,
  getHubHeadshotLookCards,
} from "@/components/dashboard/jtbd/jtbdData";
import {
  JTBD_AVATAR_HERO_IMAGES,
  JTBD_HEADSHOT_HERO_IMAGES,
} from "@/components/dashboard/jtbd/jtbdHeroMedia";

export type CatalogNavKind = "studio" | "preset" | "free";

export type CatalogNavLook = {
  images: string[];
  cardType: "look" | "style";
  overlaySrc?: string;
  borderFrames?: Array<{ color: string; bg: string }>;
};

export type CatalogNavItem = {
  id: string;
  title: string;
  tagline?: string;
  href: string;
  poster: string;
  kind: CatalogNavKind;
  sectionId: string;
  toolId: string;
  badge?: "free";
  look?: CatalogNavLook;
  preset?: PhotoEditorTool;
};

export type CatalogNavSection = {
  id: string;
  title: string;
  tagline?: string;
  items: CatalogNavItem[];
};

function firstImage(images: string[] | undefined): string {
  return images?.find(Boolean) ?? "";
}

function presetPoster(tool: PhotoEditorTool): string {
  const preview = tool.preview;
  if (preview?.type === "fade-cycle") return firstImage(preview.images);
  if (preview?.type === "before-after") return preview.pairs[0]?.before ?? "";
  if (preview?.type === "static") return preview.image ?? "";
  return getPhotoEditorPreviewPoster(preview) ?? "";
}

function studioHeadshots(): CatalogNavItem {
  const look = getHubHeadshotLookCards()[0];
  const images = look?.images?.length ? look.images : JTBD_HEADSHOT_HERO_IMAGES;
  return {
    id: "ai-headshots",
    title: "AI Headshots",
    tagline: "Studio-quality, from one selfie.",
    href: "/editor-v2/ai-headshots",
    poster: firstImage(images),
    kind: "studio",
    sectionId: "studios",
    toolId: "ai-headshots",
    look: { images, cardType: "look" },
  };
}

function studioPhotoshoot(): CatalogNavItem {
  const look = getHubBrandingLookCards(1)[0];
  const images = look?.images?.length ? look.images : JTBD_AVATAR_HERO_IMAGES;
  return {
    id: "ai-avatars",
    title: "AI Photoshoot",
    tagline: "Lifestyle photos for dating, work, and everyday.",
    href: "/editor-v2/ai-avatars",
    poster: firstImage(images),
    kind: "studio",
    sectionId: "studios",
    toolId: "ai-avatars",
    look: { images, cardType: "style" },
  };
}

function presetItem(tool: PhotoEditorTool, sectionId: string): CatalogNavItem {
  return {
    id: tool.id,
    title: tool.title,
    tagline: tool.tagline,
    href: buildPresetHref(tool.id),
    poster: presetPoster(tool),
    kind: "preset",
    sectionId,
    toolId: "ai-photo-editor",
    preset: tool,
  };
}

function freeItems(): CatalogNavItem[] {
  return getHubFreeToolCards().map((card) => ({
    id: card.id,
    title: card.title,
    href: card.href,
    poster: firstImage(card.images) || card.overlaySrc || "",
    kind: "free" as const,
    sectionId: "free-pfpmaker",
    toolId: card.toolId,
    badge: "free" as const,
    look: {
      images: card.images,
      cardType: "style" as const,
      overlaySrc: card.overlaySrc,
      borderFrames: card.borderFrames,
    },
  }));
}

/** Same catalog the dashboard shows: studios, each live preset, then free tools. */
export function getCatalogNavSections(): CatalogNavSection[] {
  const studios: CatalogNavSection = {
    id: "studios",
    title: "Create",
    items: [studioHeadshots(), studioPhotoshoot()],
  };

  const jtbds = getPhotoEditorJtbdCategories()
    .map((category) => ({
      id: category.id,
      title: category.title.replace(/ Tools$/, ""),
      tagline: category.tagline,
      items: getToolsForCategory(category.id).map((tool) => presetItem(tool, category.id)),
    }))
    .filter((section) => section.items.length > 0);

  const free: CatalogNavSection = {
    id: "free-pfpmaker",
    title: "Free tools",
    items: freeItems(),
  };

  return [studios, ...jtbds, ...(free.items.length > 0 ? [free] : [])];
}

export function getCatalogNavItems(): CatalogNavItem[] {
  return getCatalogNavSections().flatMap((section) => section.items);
}

export function isCatalogNavActive(item: CatalogNavItem, activeId: string): boolean {
  if (!activeId) return false;
  if (item.id === activeId) return true;
  if (item.kind === "studio" && item.toolId === activeId) return true;
  if (item.kind === "free" && (item.toolId === activeId || item.id === activeId)) return true;
  return false;
}
